/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {NodeType} from '@fluxer/markdown_parser/src/types/Enums';
import type {LinkNode, ListNode, Node, TableNode, TableRowNode, TextNode} from '@fluxer/markdown_parser/src/types/Nodes';

function nodeHasStyleableText(node: Node): boolean {
	switch (node.type) {
		case NodeType.Text:
			return (node as TextNode).content.trim().length > 0;

		case NodeType.Link: {
			const linkNode = node as LinkNode;
			return linkNode.text ? nodeHasStyleableText(linkNode.text) : linkNode.url.trim().length > 0;
		}

		case NodeType.Blockquote:
		case NodeType.Strong:
		case NodeType.Emphasis:
		case NodeType.Underline:
		case NodeType.Strikethrough:
		case NodeType.Spoiler:
		case NodeType.Heading:
		case NodeType.Subtext:
		case NodeType.Sequence:
		case NodeType.Alert:
		case NodeType.TableCell:
			return node.children.some(nodeHasStyleableText);

		case NodeType.List:
			return (node as ListNode).items.some((item) => item.children.some(nodeHasStyleableText));

		case NodeType.Table: {
			const tableNode = node as TableNode;
			return (
				nodeHasStyleableText(tableNode.header) ||
				tableNode.rows.some((row) => nodeHasStyleableText(row))
			);
		}

		case NodeType.TableRow:
			return (node as TableRowNode).cells.some(nodeHasStyleableText);

		case NodeType.CodeBlock:
		case NodeType.InlineCode:
		case NodeType.Timestamp:
			return true;

		case NodeType.Mention:
		case NodeType.Emoji:
			return false;

		default:
			return false;
	}
}

export function hasStyleableMessageText(nodes: ReadonlyArray<Node>): boolean {
	return nodes.some(nodeHasStyleableText);
}
